import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';

// Consulting layer (spec 11.4): the weak-signal / pre-breach tier. Signals approaching their
// tripwire (distance + velocity, most-imminent first) and assumption pressure (contradicting
// vs supporting movement). Read-only; surfaced before anything fires.

const TREND_TAG: Record<string, string> = { rising: 'red', easing: 'teal', flat: 'muted' };

export default function Emerging() {
  const emerging = useQuery({ queryKey: ['emerging'], queryFn: api.emergingSignals });
  const pressure = useQuery({ queryKey: ['assumption-pressure'], queryFn: api.assumptionPressure });

  return (
    <div>
      <div className="ph">Consulting · Emerging / Pre-Breach</div>
      <div className="h2">Pressure building before anything fires</div>
      <p className="lead">Signals approaching a tripwire, and the assumptions under the most strain.</p>

      <div className="panel" style={{ marginBottom: 14 }}>
        <div className="qhead"><span className="qdot" style={{ background: 'var(--amber)' }} />Emerging signals · most imminent first</div>
        {emerging.isLoading ? (
          <div className="small faint">Loading…</div>
        ) : (emerging.data?.signals.length ?? 0) === 0 ? (
          <div className="small faint">No signals are currently emerging toward a breach.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Signal</th>
                <th>Money node</th>
                <th className="center">Distance</th>
                <th className="center">Velocity</th>
                <th className="center">~Refreshes to breach</th>
                <th className="center">Would-be severity</th>
              </tr>
            </thead>
            <tbody>
              {emerging.data?.signals.map((s) => (
                <tr key={s.signal_id}>
                  <td>{s.signal_id} <span className="small faint">{s.signal_name}</span></td>
                  <td className="small">{s.money_node_id}</td>
                  <td className="center mono">{s.distance_to_breach}</td>
                  <td className="center mono">{s.velocity}</td>
                  <td className="center mono">{s.steps_to_breach}</td>
                  <td className="center"><span className="tag amber">{s.would_be_severity}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="panel">
        <div className="qhead"><span className="qdot" style={{ background: 'var(--teal)' }} />Assumption pressure</div>
        {pressure.isLoading ? (
          <div className="small faint">Loading…</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Assumption</th>
                <th className="center">Pressure</th>
                <th className="center">Trend</th>
                <th className="center">Contradicting</th>
                <th className="center">Supporting</th>
              </tr>
            </thead>
            <tbody>
              {pressure.data?.assumptions.map((a) => (
                <tr key={a.assumption_id}>
                  <td>{a.assumption_id} <span className="small faint">{a.statement}</span></td>
                  <td className="center mono">{a.pressure}</td>
                  <td className="center"><span className={`tag ${TREND_TAG[a.trend] ?? 'muted'}`}>{a.trend}</span></td>
                  <td className="center small">{a.contradicting_signal_ids.join(', ') || '—'}</td>
                  <td className="center small">{a.supporting_signal_ids.join(', ') || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
